import { CarbonEmissionsOrganizationResponse } from "Api/carbonEmissions/carbonEmissions.types";

export type MonthlyCarbonEmission = {
  month: string; // e.g., "2023-01"
  value: number;
};

export type NormalizedCarbonEmissionsData = {
  organizationId: string;
  total: {
    previousYear: number;
    currentYear: number;
    lastSixMonths: number;
  };
  monthlyData: MonthlyCarbonEmission[];
};

export type CarbonEmissionsTimeframe =
  | "previousYear"
  | "currentYear"
  | "lastSixMonths";

/**
 * Extract monthly data for the last six months from emissions response
 */

export const extractMonthlyEmissionsFromResponse = (
  data: CarbonEmissionsOrganizationResponse
): MonthlyCarbonEmission[] => {
  if (!data || !data.projects || data.projects.length === 0) {
    return [];
  }

  // Extract values from all projects
  const monthlyData: MonthlyCarbonEmission[] = [];
  let hasValues = false;

  data.projects.forEach(project => {
    if (project.values && project.values.length > 0) {
      hasValues = true;
      project.values.forEach(value => {
        const month = value.start_time.substring(0, 7); // Get YYYY-MM format

        // Find if we already have an entry for this month
        const existingMonth = monthlyData.find(m => m.month === month);

        if (existingMonth) {
          // Add to existing month
          existingMonth.value += value.value;
        } else {
          // Create new month entry
          monthlyData.push({
            month,
            value: value.value
          });
        }
      });
    }
  });

  if (!hasValues) {
    return [];
  }

  // Convert the record object to an array and sort by month
  return Object.values(monthlyData).sort((a, b) =>
    a.month.localeCompare(b.month)
  );
};

export const normalizeCarbonEmissionsData = (
  data: CarbonEmissionsOrganizationResponse,
  organizationId: string,
  timeframe?: CarbonEmissionsTimeframe,
  existingData?: NormalizedCarbonEmissionsData | null
): NormalizedCarbonEmissionsData => {
  // Create a new data object or use the existing one
  const normalizedData: NormalizedCarbonEmissionsData = existingData || {
    organizationId,
    total: {
      previousYear: 0,
      currentYear: 0,
      lastSixMonths: 0
    },
    monthlyData: []
  };

  // Get the total from the API response
  const total = data.total || 0;

  // Update the appropriate timeframe's total
  if (timeframe) {
    normalizedData.total[timeframe] = total;
  }

  // Extract and update monthly data if this is the lastSixMonths timeframe
  if (timeframe === "lastSixMonths") {
    normalizedData.monthlyData = extractMonthlyEmissionsFromResponse(data);
  }

  return normalizedData;
};

export const extractMonthlyEmissionsFromOrgData = (
  data?: NormalizedCarbonEmissionsData | null
): MonthlyCarbonEmission[] => {
  if (!data) {
    return [];
  }

  return data.monthlyData || [];
};
